import { createPromise } from 'redux-promise-middleware';
import { createStore, applyMiddleware, combineReducers, compose } from 'redux';
import { thunk } from 'redux-thunk';
import createLogger from 'redux-logger';
import customMiddlewares from '../middleware';
import customReducers from '../modules';
// XXX Do not import config like this, should be injected into the initial state data.
import config from '../../config/config';
import { isPromise } from '../../utils/utils';

export function errorMiddleware() {
  return (next) => (action) => {
    if (!isPromise(action.payload)) {
      return next(action);
    }

    return next(action).catch((error) => {
      console.error('Error:', error);
      return error;
    });
  };
}

const defaultData = {};
const middlewares = [
  errorMiddleware,
  thunk,
  createPromise({
    promiseTypeSuffixes: ['PENDING', 'FULFILLED', 'REJECTED'],
  }),
  // any custom middlewares here
  ...customMiddlewares,
];

// we only want the redux logger during dev, or other envs configured for it
if (config.debug.enableReduxLogger) {
  Error.stackTraceLimit = 50;
  const loggerconfig = {
    // https://github.com/fcomb/redux-logger

    actionTransformer: (action) => {
      action.loggedStack = new Error().stack.split('\n').slice(2);
      return action;
    },
  };
  const logger = createLogger(loggerconfig);
  // Logger must be last middleware in chain, otherwise it will log thunk and promise, not actual actions
  middlewares.push(logger);
}

const storeExtensions = [
  // redux supports 'middlewares', which are like $http interceptors,
  // or server-side middleware. Here, we add them to the store.
  // SEE: http://rackt.github.io/redux/docs/advanced/Middleware.html
  applyMiddleware(...middlewares),
];

if (config.debug.enableReduxDevTools) {
  // activate Redux DevTools Chrome extension if active
  // see: https://github.com/zalmoxisus/redux-devtools-extension/commit/6c146a2e16da79fefdc0e3e33f188d4ee6667341
  const devToolsEnabler = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__()
    : (func) => func;
  storeExtensions.push(devToolsEnabler);
}

export default function (data = defaultData) {
  // This creates a functional constructor for our single redux store.
  // If the redux devtools are active, we compose them into the store.
  // Else, just apply our middlewares to the store.

  // `compose` is a functional programming convenience utility, from Redux.
  // It's a bit like object.assign(), but for functions.
  // SEE: http://rackt.github.io/redux/docs/api/compose.html
  const finalCreateStore = compose(...storeExtensions)(createStore);

  // Above, we imported many small component reducers.
  // Here, we combine them into one big uber-reducer:
  // This assembles a monolithic reducer for our monolithic store.
  const reducer = combineReducers({
    // our own custom reducers, from the index file:
    ...customReducers,
    // any library reducers:
    // routing: routerReducer,
  });

  // create our single redux store, and bind the single reducer to it
  return finalCreateStore(reducer, data);
}
